import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Search, Users, FileSignature, Inbox, Scale, ArrowRight, Folder, Building2 } from "lucide-react";

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ clients: [], cases: [], contracts: [], messages: [], legal: [], documents: [], governing_bodies: [] });
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResults({ clients: [], cases: [], contracts: [], messages: [], legal: [], documents: [], governing_bodies: [] }); return; }
  }, [open]);

  useEffect(() => {
    if (!q || q.length < 1) {
      setResults({ clients: [], cases: [], contracts: [], messages: [], legal: [], documents: [], governing_bodies: [] });
      return;
    }
    const id = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data));
    }, 200);
    return () => clearTimeout(id);
  }, [q]);

  if (!open) return null;

  const go = (path) => { onClose(); navigate(path); };

  const actions = [
    { label: "Add client", path: "/clients/new", icon: Users, match: ["add", "client", "new"] },
    { label: "Open inbox", path: "/inbox", icon: Inbox, match: ["inbox", "messages"] },
    { label: "Open cases", path: "/cases", icon: FileSignature, match: ["cases"] },
    { label: "Legal hub", path: "/legal", icon: Scale, match: ["legal", "tms"] },
  ].filter((a) => !q || a.match.some((m) => m.includes(q.toLowerCase())) || a.label.toLowerCase().includes(q.toLowerCase()));

  const totalResults =
    results.clients.length + results.cases.length + results.contracts.length +
    results.messages.length + results.legal.length +
    (results.documents?.length || 0) + (results.governing_bodies?.length || 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 bg-slate-900/40 backdrop-blur-sm"
         data-testid="command-palette" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Search size={16} className="text-slate-400" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
            placeholder="Search clients, cases, contracts, messages…  or type a command"
            className="flex-1 bg-transparent outline-none text-base"
            data-testid="palette-input"
          />
          <kbd className="text-[10px] font-mono-tab text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto pm-scroll">
          {/* Actions */}
          {actions.length > 0 && (
            <Section title="Actions">
              {actions.map((a) => (
                <Row key={a.path} icon={a.icon} title={a.label} sub="Go" onClick={() => go(a.path)} testid={`pal-action-${a.path.replace(/\W+/g, '-')}`} />
              ))}
            </Section>
          )}
          {/* Clients */}
          {results.clients.length > 0 && (
            <Section title="Clients">
              {results.clients.map((c) => (
                <Row key={c.id} icon={Users} title={c.full_name} sub={`${c.position} · ${c.current_club}`}
                     photo={c.photo_url} onClick={() => go(`/clients/${c.id}`)} testid={`pal-client-${c.id}`} />
              ))}
            </Section>
          )}
          {results.cases.length > 0 && (
            <Section title="Cases">
              {results.cases.map((c) => (
                <Row key={c.id} icon={FileSignature} title={c.title} sub={`${c.priority || ""} · ${c.status || ""}`}
                     onClick={() => go(`/cases/${c.id}`)} testid={`pal-case-${c.id}`} />
              ))}
            </Section>
          )}
          {results.contracts.length > 0 && (
            <Section title="Contracts">
              {results.contracts.map((c) => (
                <Row key={c.id} icon={FileSignature} title={c.title} sub={c.doc_type?.replace("_", " ")}
                     onClick={() => go(`/clients/${c.client_id}?tab=contracts`)} testid={`pal-contract-${c.id}`} />
              ))}
            </Section>
          )}
          {results.messages.length > 0 && (
            <Section title="Messages">
              {results.messages.map((m) => (
                <Row key={m.id} icon={Inbox} title={m.subject || m.sender_name} sub={`${m.channel} · ${m.sender_name}`}
                     onClick={() => go(`/inbox`)} testid={`pal-msg-${m.id}`} />
              ))}
            </Section>
          )}
          {results.legal.length > 0 && (
            <Section title="Legal">
              {results.legal.map((l) => (
                <Row key={l.id} icon={Scale} title={l.case_number} sub={`${l.case_type?.replace("_", " ")} · ${l.status}`}
                     onClick={() => go(`/legal`)} testid={`pal-legal-${l.id}`} />
              ))}
            </Section>
          )}
          {(results.documents || []).length > 0 && (
            <Section title="Documents">
              {results.documents.map((d) => (
                <Row key={d.id} icon={Folder} title={d.title} sub={`${d.author || ""} · ${d.tag || ""}`}
                     onClick={() => go(`/scouting-docs`)} testid={`pal-doc-${d.id}`} />
              ))}
            </Section>
          )}
          {(results.governing_bodies || []).length > 0 && (
            <Section title="Governing bodies">
              {results.governing_bodies.map((g) => (
                <Row key={g.id} icon={Building2} title={g.governing_body || g.case_number} sub={`${g.case_number} · ${g.case_type?.replace("_", " ") || ""}`}
                     onClick={() => go(`/legal`)} testid={`pal-gov-${g.id}`} />
              ))}
            </Section>
          )}
          {q && totalResults === 0 && actions.length === 0 && (
            <div className="text-sm text-slate-500 px-6 py-10 text-center">No results for "{q}"</div>
          )}
          {!q && (
            <div className="text-xs text-slate-500 px-6 py-6 text-center space-y-1">
              <div>Try: <span className="font-mono-tab font-bold">Lucas</span>, <span className="font-mono-tab font-bold">Arsenal</span>, <span className="font-mono-tab font-bold">add client</span>, <span className="font-mono-tab font-bold">TMS</span></div>
              <div className="text-slate-400">⌘K to open · ESC to close</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="px-2 py-1">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 px-2 py-1.5">{title}</div>
      {children}
    </div>
  );
}

function Row({ icon: Icon, title, sub, onClick, photo, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 text-left transition-colors"
    >
      {photo ? (
        <img src={photo} alt="" className="w-8 h-8 rounded-lg object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
          <Icon size={14} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-900 truncate">{title}</div>
        <div className="text-xs text-slate-500 truncate">{sub}</div>
      </div>
      <ArrowRight size={14} className="text-slate-300" />
    </button>
  );
}
